import { notification } from "antd";
import { useTranslation } from "react-i18next";

interface FieldError {
  field: string;
  messages: string[] | string;
}

export default function useNotification() {
  const { t } = useTranslation();

  const notificationError = (errors: FieldError[] | undefined, name?: string) => {
    errors?.forEach((error: FieldError) => {
      const allVariants = ["_All__", "__All__", "__all__", "_all__"];
      const fieldError = allVariants.includes(error.field) ? t("Error") : t(name ?? "Error");

      const messages = Array.isArray(error?.messages) ? error.messages : [error?.messages];
      messages.forEach((message: string | string[]) => {
        notification.error({
          message: fieldError,
          duration: 10,
          description: Array.isArray(message) ? message.join(". ") : message,
          placement: "bottomLeft",
        });
      });
    });
  };

  const notificationLineError = (errors: FieldError[]) => {
    errors.forEach((error: FieldError) => {
      const messages = Array.isArray(error.messages) ? error.messages : [error.messages];
      messages.forEach((message: string) => {
        notification.error({
          message: error.field.split("_").slice(-1).pop(),
          description: message,
          placement: "bottomLeft",
        });
      });
    });
  };

  const success = (title: string, description: string) => {
    notification.success({
      message: t(title),
      description: t(description),
      placement: "bottomLeft",
    });
  };

  const error = (title: string, description: string) => {
    notification.error({
      message: t(title),
      description: t(description),
      placement: "bottomLeft",
    });
  };

  return {
    notificationError,
    notificationLineError,
    notificationErrorProduce: (title: string) => error(title, "Cannot be produced"),
    notificationErrorDefault: (title: string) =>
      error(title, "There was an error try again later or contact your support"),
    notificationErrorAccepted: (title: string) => error(title, "Cannot be accepted"),
    notificationErrorValidate: (title: string) => error(title, "Cannot be validated"),
    notificationSuccessfullySent: (title: string) => success(title, "Successfully sent"),
    notificationSuccessfullySaved: (title: string) => success(title, "Saved Successfully"),
    notificationSuccessfullyAnulled: (title: string) => success(title, "Anulled"),
    notificationSuccessfullyRefused: (title: string) => success(title, "Refused"),
    notificationSuccessfullyDeleted: (title: string) => success(title, "Deleted Successfully"),
    notificationSuccessfullyCreated: (title: string) => success(title, "Successfully created"),
    notificationSuccessfullyImported: (title: string) => success(title, "Successfully imported"),
    notificationSuccessfullyProduced: (title: string) => success(title, "Produced"),
    notificationSuccessfullyAccepted: (title: string) => success(title, "Accepted"),
    notificationSuccessfullyValidated: (title: string) => success(title, "Validated"),
    notificationSuccessfullyDuplicated: (title: string) =>
      success(title, "Duplicated Successfully"),
    notificationSuccessfullyTransferred: (title: string) =>
      success(title, "Successfully transferred"),
  };
}
